import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import type { SystemGroupUpdateRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { createGroupFormPermissions, toGroupPermissionsRequest } from "@/helpers/system-groups/permissions";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { platformGroupMutations } from "@/queries/platform-groups/mutations";
import { usePlatformGroupQueries } from "@/queries/platform-groups/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { type FormValues, PlatformGroupForm } from "./PlatformGroupForm";

export function EditPlatformGroupPage(): React.ReactNode {
  const { id: groupId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const { data: projects = [] } = useLinkedProjects();
  const permissionQueries = usePermissionQueries();
  const platformGroupQueries = usePlatformGroupQueries();
  const groupQuery = useQuery(platformGroupQueries.details(groupId!));
  const defaultPermissionsQuery = useQuery(permissionQueries.listDefaultTypes());
  const rolesQuery = useQuery(permissionQueries.listRoles());
  const updateGroup = platformGroupMutations.useUpdateGroup();

  const error = groupQuery.error || defaultPermissionsQuery.error || rolesQuery.error;
  if (error) {
    return <ErrorPage error={error} />;
  }
  if (groupQuery.isFetching || defaultPermissionsQuery.isFetching || rolesQuery.isFetching || !groupQuery.data) {
    return <FullSizeLoader withPadding />;
  }
  const group = groupQuery.data;

  const handleSubmit = async (values: FormValues) => {
    if (!values.image?.length) {
      showFlashToast({ variant: "alert", message: t("page.platform-groups.create.error") });

      return;
    }
    const image = await uploadFormImage(values.image[0]);
    if (!image) {
      return;
    }
    const payload: SystemGroupUpdateRequest = {
      type: values.type,
      name: values.name,
      description: values.description,
      icon: values.icon!,
      imageId: image.id,
      defaultPostingAudience: values.audience,
      permissions: toGroupPermissionsRequest(values.permissions),
    };
    await updateGroup.mutateAsync({ id: groupId!, payload });
    void navigate(routes.platformGroups.list());
  };

  const translations = group.translations ?? [];
  const originalTranslation = translations.find((translation) => translation.languageId === group.originalLanguageId);

  return (
    <PlatformGroupForm
      isEditMode
      projects={projects}
      defaultValues={{
        type: group.type,
        name: originalTranslation?.name || translations[0]?.name || "",
        description: originalTranslation?.description || translations[0]?.description || "",
        icon: group.icon,
        image: group.image ? [group.image] : [],
        projects: group.excludedProjects
          ? projects.filter((project) => !group.excludedProjects?.some((excluded) => excluded.id === project.id))
          : projects,
        postingLevel: group.postingLevel || "projectAndProjectConnection",
        audience: group.defaultPostingAudience || "projectConnection",
        permissions: createGroupFormPermissions(
          rolesQuery.data ?? [],
          defaultPermissionsQuery.data ?? [],
          group.permissions,
        ),
      }}
      onSubmit={handleSubmit}
      isSubmitting={isUploadingImage || updateGroup.isPending}
    />
  );
}
